import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../lib/ui";
import { Loader2, AlertTriangle, Lightbulb, Eye, Compass, MapPin, User, ClipboardPlus } from "lucide-react";
import { ActionForm } from "./Actions";

const OV = ["provincial_supervisor", "si_analyst", "program_manager", "admin"];
const KIND = {
  defis: { icon: AlertTriangle, cls: "bg-red-50 text-red-700 border-red-200" },
  recommendations: { icon: Lightbulb, cls: "bg-amber-50 text-amber-800 border-amber-200" },
  situation_interp: { icon: Eye, cls: "bg-sky-50 text-sky-700 border-sky-200" },
  perspectives: { icon: Compass, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};
const STATUS = { new: "Nouveau", in_progress: "En cours", resolved: "Résolu" };
const STATUS_CLS = { new: "bg-slate-100 text-slate-700", in_progress: "bg-[#FFF3EE] text-[#F05A28]", resolved: "bg-emerald-100 text-emerald-800" };

const Sel = ({ value, onChange, children, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700">{children}</select>
);

export default function Terrain() {
  const { t } = useLang();
  const { user } = useAuth();
  const canEdit = OV.includes(user?.role);
  const [data, setData] = useState(null);
  const [f, setF] = useState({ days: "30", province: "", health_zone: "", kind: "", status: "" });
  const [acting, setActing] = useState("");

  const load = useCallback(async () => {
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const r = await api.get("/harmonized/terrain", { params });
      setData(r.data);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (item, status) => {
    try {
      const r = await api.put(`/harmonized/terrain/${encodeURIComponent(item.key)}/status`, { status, note: item.note });
      setData((d) => ({ ...d, items: d.items.map((i) => (i.key === item.key ? { ...i, ...r.data } : i)) }));
      toast.success(t("Statut mis à jour."));
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const upd = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  return (
    <div data-testid="terrain-page">
      <PageHeader eyebrow={t("Remontées des zones de santé")} title={t("Terrain")}
        subtitle={t("Observations, défis, recommandations et perspectives de tous les épidémiologistes, en un seul fil. La coordination suit chaque point jusqu'à sa résolution.")} testid="terrain-header" />

      {data && (
        <div className="grid grid-cols-3 gap-3 mb-4" data-testid="terrain-counts">
          {Object.entries(STATUS).map(([k, lbl]) => (
            <button key={k} type="button" onClick={() => upd("status")(f.status === k ? "" : k)}
              className={`rounded-lg border p-3 text-left transition-colors ${f.status === k ? "border-[#F05A28] bg-[#FFF8F5]" : "border-slate-200 bg-white hover:border-slate-300"}`} data-testid={`terrain-count-${k}`}>
              <p className="text-[11px] uppercase tracking-wider text-slate-500">{t(lbl)}</p>
              <p className="text-2xl font-semibold text-slate-900">{data.counts[k]}</p>
            </button>
          ))}
        </div>
      )}

      <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-3 mb-4 flex flex-wrap gap-2" data-testid="terrain-filters">
        <Sel value={f.days} onChange={upd("days")} testid="terrain-f-days">
          <option value="7">{t("7 derniers jours")}</option><option value="30">{t("30 derniers jours")}</option><option value="90">{t("90 derniers jours")}</option><option value="365">{t("12 mois")}</option>
        </Sel>
        <Sel value={f.province} onChange={upd("province")} testid="terrain-f-province">
          <option value="">{t("Toutes les provinces")}</option>{(data?.provinces || []).map((p) => <option key={p} value={p}>{p}</option>)}
        </Sel>
        <Sel value={f.health_zone} onChange={upd("health_zone")} testid="terrain-f-zone">
          <option value="">{t("Toutes les zones de santé")}</option>{(data?.zones || []).map((z) => <option key={z} value={z}>{z}</option>)}
        </Sel>
        <Sel value={f.kind} onChange={upd("kind")} testid="terrain-f-kind">
          <option value="">{t("Tous les types")}</option>
          <option value="defis">{t("Défis")}</option><option value="recommendations">{t("Recommandations")}</option>
          <option value="situation_interp">{t("Observations")}</option><option value="perspectives">{t("Perspectives")}</option>
        </Sel>
      </div>

      {!data ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#F05A28]" /></div> : data.items.length === 0 ? (
        <p className="text-sm text-slate-500 italic py-10 text-center" data-testid="terrain-empty">{t("Aucune remontée pour ces filtres.")}</p>
      ) : (
        <div className="space-y-3" data-testid="terrain-list">
          {data.items.map((it) => {
            const K = KIND[it.kind] || KIND.situation_interp;
            return (
              <article key={it.key} className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-4" data-testid={`terrain-item-${it.key}`}>
                <div className="flex flex-wrap items-center gap-2 mb-2 text-xs">
                  <span className={`inline-flex items-center gap-1 border rounded px-2 py-0.5 font-semibold ${K.cls}`}><K.icon className="h-3 w-3" />{t(it.kind_label)}</span>
                  <span className="inline-flex items-center gap-1 text-slate-600"><MapPin className="h-3 w-3" />{it.health_zone || "—"}{it.province ? ` · ${it.province}` : ""}</span>
                  <span className="inline-flex items-center gap-1 text-slate-500"><User className="h-3 w-3" />{it.author_name}</span>
                  <span className="text-slate-400">{it.date}</span>
                  <span className={`ml-auto rounded px-2 py-0.5 font-semibold ${STATUS_CLS[it.status]}`} data-testid={`terrain-status-${it.key}`}>{t(STATUS[it.status])}</span>
                </div>
                <p className="text-sm text-slate-800 whitespace-pre-line">{it.text}</p>
                {canEdit && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {Object.entries(STATUS).map(([k, lbl]) => (
                      <button key={k} type="button" onClick={() => setStatus(it, k)} disabled={it.status === k}
                        className={`text-xs rounded-md px-2.5 py-1 border transition-colors ${it.status === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
                        data-testid={`terrain-set-${k}-${it.key}`}>{t(lbl)}</button>
                    ))}
                    {it.updated_by && <span className="text-[11px] text-slate-400">{t("par")} {it.updated_by}</span>}
                    {it.action_id ? (
                      <a href="/actions" className="ml-auto text-xs font-semibold text-[#F05A28] hover:underline" data-testid={`terrain-action-link-${it.key}`}>{t("Voir l'action SMART")}</a>
                    ) : (
                      <button type="button" onClick={() => setActing(acting === it.key ? "" : it.key)} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-[#F05A28] hover:underline" data-testid={`terrain-make-action-${it.key}`}><ClipboardPlus className="h-3.5 w-3.5" />{t("Créer une action SMART")}</button>
                    )}
                  </div>
                )}
                {acting === it.key && (
                  <div className="mt-3">
                    <ActionForm initial={{ problem: it.text.slice(0, 400), health_zone: it.health_zone, province: it.province, source_key: it.key, source_text: it.text.slice(0, 400) }}
                      onSaved={() => { setActing(""); load(); }} onCancel={() => setActing("")} />
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
